import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, money, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const StatCard = ({ label, value, testId }) => (
  <div data-testid={testId} className="border border-black/10 bg-white p-5">
    <p className="text-[10px] font-bold uppercase tracking-widest text-black/40">{label}</p>
    <p className="font-heading font-black text-2xl mt-1">{value}</p>
  </div>
);

function SubscribersTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/subscribers").then(({ data }) => setData(data)).catch(() => toast.error("Could not load subscribers")); }, []);

  const exportCsv = () => {
    const rows = [["email", "source", "club_member", "signed_up"], ...data.subscribers.map((s) => [s.email, s.source || "", s.club_member ? "yes" : "no", s.created_at || ""])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "shodan-subscribers.csv";
    a.click();
  };

  if (!data) return <p className="mt-8 text-black/40 animate-pulse text-sm">Loading subscribers…</p>;
  return (
    <div className="mt-8">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <p data-testid="subscriber-count" className="text-sm font-bold uppercase tracking-widest">{data.total} Subscriber{data.total === 1 ? "" : "s"}</p>
        <button data-testid="export-subscribers-btn" onClick={exportCsv} disabled={!data.total}
          className="bg-[#121212] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150 disabled:opacity-40">
          Export CSV
        </button>
      </div>
      <div className="overflow-x-auto border border-black/10">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F3F0] text-[10px] uppercase tracking-widest">
            <tr><th className="text-left p-3">Email</th><th className="text-left p-3">Source</th><th className="text-left p-3">Club Member</th><th className="text-left p-3">Signed Up</th></tr>
          </thead>
          <tbody>
            {data.subscribers.map((s) => (
              <tr key={s.email} data-testid={`subscriber-row-${s.email}`} className="border-t border-black/5">
                <td className="p-3 font-mono text-xs">{s.email}</td>
                <td className="p-3"><span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-black/5">{s.source || "—"}</span></td>
                <td className="p-3">{s.club_member ? <span className="text-[#4A5D23] font-bold text-xs">✓ Member</span> : <span className="text-black/30 text-xs">—</span>}</td>
                <td className="p-3 text-xs text-black/50">{s.created_at ? new Date(s.created_at).toLocaleString() : "—"}</td>
              </tr>
            ))}
            {data.subscribers.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-black/40">No subscribers yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SupplierSync() {
  const [status, setStatus] = useState({});
  const load = () => api.get("/admin/sync/status").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);
  const start = async (supplier) => {
    try {
      const { data } = await api.post(`/admin/sync/${supplier}`);
      toast[data.ok ? "success" : "error"](data.message);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const Card = ({ supplier, title, desc, testPrefix }) => {
    const s = status[supplier];
    return (
      <div className="border border-black/10 p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="font-heading font-bold uppercase tracking-tight">{title}</h3>
            <p className="text-xs text-black/50 mt-1 max-w-lg">{desc}</p>
          </div>
          <button data-testid={`${testPrefix}-sync-btn`} onClick={() => start(supplier)} disabled={s?.status === "running"}
            className="bg-[#4A5D23] text-white px-6 py-3 text-xs font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150 disabled:opacity-50">
            {s?.status === "running" ? "Sync Running…" : "Run Sync Now"}
          </button>
        </div>
        {s && s.status !== "never_run" && (
          <div data-testid={`${testPrefix}-sync-status`} className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><p className="text-[10px] font-bold uppercase tracking-widest text-black/40">Status</p>
              <p className={`font-bold uppercase text-xs mt-1 ${s.status === "completed" ? "text-[#2E8B57]" : s.status === "failed" ? "text-[#8B0000]" : "text-[#B8860B]"}`}>{s.status} {s.step && s.status === "running" ? `(${s.step})` : ""}</p></div>
            <div><p className="text-[10px] font-bold uppercase tracking-widest text-black/40">Products</p><p className="font-bold mt-1">{s.products_done ?? 0}</p></div>
            <div><p className="text-[10px] font-bold uppercase tracking-widest text-black/40">Images</p><p className="font-bold mt-1">{s.images_done ?? 0}{s.images_total ? ` / ${s.images_total}` : ""}</p></div>
            <div><p className="text-[10px] font-bold uppercase tracking-widest text-black/40">Last Run</p><p className="text-xs mt-1">{s.finished_at ? new Date(s.finished_at).toLocaleString() : "—"}</p></div>
            {s.error && <p className="col-span-full text-xs text-[#8B0000] font-mono">{s.error}</p>}
          </div>
        )}
      </div>
    );
  };
  const inv = status.rothco_inventory;
  return (
    <div className="mt-8 max-w-4xl space-y-5">
      <Card supplier="rothco" testPrefix="rothco" title="Rothco Catalog Sync"
        desc="Pulls products, categories, variations, inventory and images from the Rothco GraphQL API. Style images ensure each color option shows the correct photo. Images cached on our server." />
      <Card supplier="safetytech" testPrefix="st" title="Safety Technology Catalog Sync"
        desc="Imports the Safety Technology product feed (stun guns, pepper spray, hidden cameras, alarms, diversion safes) with retail pricing, UPCs and images downloaded to our server." />
      <div className="border border-black/10 p-6">
        <h3 className="font-heading font-bold uppercase tracking-tight">Hourly Inventory Refresh</h3>
        <p className="text-xs text-black/50 mt-1">Rothco stock levels refresh automatically every hour at a randomized minute (per Rothco sync guidelines).</p>
        {inv && inv.status !== "never_run" && (
          <p data-testid="inventory-refresh-status" className="text-xs mt-3">
            Last refresh: <span className={`font-bold uppercase ${inv.status === "completed" ? "text-[#2E8B57]" : "text-[#8B0000]"}`}>{inv.status}</span>
            {inv.finished_at ? ` · ${new Date(inv.finished_at).toLocaleString()}` : ""} {inv.products_done ? ` · ${inv.products_done} products` : ""}
          </p>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(null);
  const [orderFilter, setOrderFilter] = useState("all");
  const [productSearch, setProductSearch] = useState("");

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {});
  }, [user]);

  const filteredOrders = useMemo(() => orders.filter((o) => orderFilter === "all" ? true : orderFilter === "crypto" ? o.payment_method === "usdc" : o.payment_method !== "usdc"), [orders, orderFilter]);
  const cryptoRevenue = useMemo(() => orders.filter((o) => o.payment_method === "usdc").reduce((s, o) => s + o.total, 0), [orders]);
  const shownProducts = useMemo(() => products.filter((p) => !productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()) || p.slug.includes(productSearch.toLowerCase())).slice(0, 100), [products, productSearch]);

  if (loading || !user || user.role !== "admin") return <div className="py-32 text-center text-black/40 animate-pulse text-sm uppercase tracking-widest font-bold">Loading…</div>;

  const updateOrderStatus = async (orderId, status) => {
    let tracking_number = null;
    if (status === "shipped") tracking_number = window.prompt("Tracking number (optional):") || null;
    try {
      const { data } = await api.put(`/admin/orders/${orderId}`, { status, tracking_number });
      setOrders((prev) => prev.map((o) => (o.order_id === orderId ? data : o)));
      toast.success(`Order ${orderId} → ${status}`);
    } catch { toast.error("Could not update order"); }
  };

  const updateProduct = async (slug, field, value) => {
    try {
      const { data } = await api.put(`/admin/products/${slug}`, { [field]: value });
      setProducts((prev) => prev.map((p) => (p.slug === slug ? data : p)));
      toast.success("Product updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", settings);
      toast.success("Settings saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
      <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#4A5D23] font-mono">Admin</p>
      <h1 className="font-heading text-3xl md:text-4xl uppercase font-black tracking-tighter mt-1">Command Center</h1>
      <div className="flex gap-1 mt-8 border-b border-black/10 overflow-x-auto">
        {[["overview", "Overview"], ["orders", "Orders"], ["products", "Products"], ["subscribers", "Subscribers"], ["suppliers", "Suppliers"], ["settings", "Settings"]].map(([v, l]) => (
          <button key={v} data-testid={`admin-tab-${v}`} onClick={() => setTab(v)}
            className={`px-5 py-3 text-xs font-bold uppercase tracking-widest border-b-2 -mb-px whitespace-nowrap transition-colors duration-150 ${tab === v ? "border-[#4A5D23] text-[#4A5D23]" : "border-transparent text-black/50 hover:text-black"}`}>{l}</button>
        ))}
      </div>

      {tab === "overview" && stats && (
        <div className="mt-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard testId="stat-revenue" label="Revenue" value={money(stats.revenue)} />
            <StatCard testId="stat-orders" label="Orders" value={stats.order_count} />
            <StatCard testId="stat-aov" label="Avg Order Value" value={money(stats.aov)} />
            <StatCard testId="stat-signups" label="Email Signups" value={stats.email_signups} />
            <StatCard testId="stat-crypto-revenue" label="USDC Revenue" value={money(cryptoRevenue)} />
            <StatCard testId="stat-coupons" label="Coupon Uses" value={stats.coupon_uses} />
            <StatCard testId="stat-show-visits" label="Show Page Visits" value={stats.show_page_visits} />
            <StatCard testId="stat-advisor" label="Advisor Chats" value={stats.advisor_interactions} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div className="border border-black/10 p-5">
              <h3 className="text-xs font-bold uppercase tracking-widest text-black/40 mb-3">Top Products</h3>
              {stats.top_products.length === 0 ? <p className="text-sm text-black/40">No sales yet</p> :
                stats.top_products.map((p) => <div key={p.name} className="flex justify-between text-sm py-1.5 border-b border-black/5"><span>{p.name}</span><span className="font-bold">{p.qty} sold</span></div>)}
            </div>
            <div className="border border-black/10 p-5">
              <h3 className="text-xs font-bold uppercase tracking-widest text-black/40 mb-3">Traffic Sources</h3>
              {Object.entries(stats.traffic_sources).map(([src, n]) => (
                <div key={src} className="flex justify-between text-sm py-1.5 border-b border-black/5"><span className="font-mono">{src}</span><span className="font-bold">{n} events</span></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="mt-8">
          <div className="flex gap-2 mb-5">
            {[["all", "All"], ["traditional", "Card"], ["crypto", "Crypto (USDC)"]].map(([v, l]) => (
              <button key={v} data-testid={`order-filter-${v}`} onClick={() => setOrderFilter(v)}
                className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border ${orderFilter === v ? "bg-[#121212] text-white border-black" : "border-black/20"}`}>{l}</button>
            ))}
          </div>
          <div className="overflow-x-auto border border-black/10">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F3F0] text-[10px] uppercase tracking-widest font-bold text-black/50">
                <tr><th className="text-left p-3">Order</th><th className="text-left p-3">Customer</th><th className="text-left p-3">Payment</th><th className="text-left p-3">Status</th><th className="text-left p-3">Fulfillment</th><th className="text-left p-3">Txn ID</th><th className="text-right p-3">Total</th><th className="text-left p-3">Date</th></tr>
              </thead>
              <tbody>
                {filteredOrders.map((o) => (
                  <tr key={o.order_id} data-testid={`admin-order-${o.order_id}`} className="border-t border-black/5">
                    <td className="p-3 font-mono font-bold">{o.order_id}</td>
                    <td className="p-3">{o.email}</td>
                    <td className="p-3"><span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${o.payment_method === "usdc" ? "bg-[#4A5D23] text-white" : "bg-black/5"}`}>{o.payment_method === "usdc" ? "USDC" : "Card"}</span>
                      {o.payment_status !== "paid" && <span className="ml-1 text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-[#B8860B] text-white">{o.payment_status}</span>}
                    </td>
                    <td className="p-3">
                      <select data-testid={`admin-order-status-${o.order_id}`} value={o.status}
                        onChange={(e) => updateOrderStatus(o.order_id, e.target.value)}
                        disabled={o.payment_status !== "paid"}
                        className="border border-black/20 px-2 py-1.5 text-xs bg-white disabled:opacity-40">
                        {o.status === "awaiting_payment" && <option value="awaiting_payment" disabled>awaiting payment</option>}
                        <option value="confirmed">confirmed</option>
                        <option value="processing">processing</option>
                        <option value="shipped">shipped</option>
                        <option value="delivered">delivered</option>
                        <option value="cancelled">cancelled</option>
                      </select>
                      {o.tracking_number && <p className="text-[10px] font-mono text-black/40 mt-1">#{o.tracking_number}</p>}
                    </td>
                    <td className="p-3">
                      {o.dropship ? (
                        <span title={o.dropship.message || o.dropship.error || ""} className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${o.dropship.status === "submitted" ? "bg-[#2E8B57] text-white" : o.dropship.status === "failed" || o.dropship.status === "rejected" ? "bg-[#8B0000] text-white" : "bg-black/10"}`}>
                          Rothco {o.dropship.status}{o.dropship.is_sandbox ? " (sandbox)" : ""}
                        </span>
                      ) : <span className="text-[10px] text-black/30 uppercase tracking-widest font-bold">—</span>}
                    </td>
                    <td className="p-3 font-mono text-xs text-black/50">{o.payment_transaction_id}</td>
                    <td className="p-3 text-right font-bold">{money(o.total)}</td>
                    <td className="p-3 text-xs text-black/50">{new Date(o.created_at).toLocaleString()}</td>
                  </tr>
                ))}
                {filteredOrders.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-black/40">No orders</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "products" && (
        <div className="mt-8">
          <input data-testid="admin-product-search" value={productSearch} onChange={(e) => setProductSearch(e.target.value)}
            placeholder="Search products by name or SKU…"
            className="w-full max-w-md border border-black/20 px-4 py-3 text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
          <div className="overflow-x-auto border border-black/10">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F3F0] text-[10px] uppercase tracking-widest font-bold text-black/50">
              <tr><th className="text-left p-3">Product</th><th className="text-left p-3">Category</th><th className="text-left p-3">Price</th><th className="text-left p-3">Stock</th><th className="text-left p-3">Featured</th><th className="text-left p-3">New Drop</th><th className="text-left p-3">Hidden</th></tr>
            </thead>
            <tbody>
              {shownProducts.map((p) => (
                <tr key={p.slug} className="border-t border-black/5">
                  <td className="p-3 font-semibold">{p.name}</td>
                  <td className="p-3 text-xs text-black/50">{p.category}</td>
                  <td className="p-3">
                    <input data-testid={`admin-price-${p.slug}`} type="number" step="0.01" defaultValue={p.price}
                      onBlur={(e) => { const v = parseFloat(e.target.value); if (v && v !== p.price) updateProduct(p.slug, "price", v); }}
                      className="w-24 border border-black/20 px-2 py-1 text-sm" />
                  </td>
                  <td className="p-3">
                    <input type="number" defaultValue={p.stock}
                      onBlur={(e) => { const v = parseInt(e.target.value); if (!isNaN(v) && v !== p.stock) updateProduct(p.slug, "stock", v); }}
                      className="w-20 border border-black/20 px-2 py-1 text-sm" />
                  </td>
                  <td className="p-3">
                    <input data-testid={`admin-featured-${p.slug}`} type="checkbox" checked={p.featured} onChange={(e) => updateProduct(p.slug, "featured", e.target.checked)} className="accent-[#4A5D23] w-4 h-4" />
                  </td>
                  <td className="p-3">
                    <input data-testid={`admin-newarrival-${p.slug}`} type="checkbox" checked={!!p.new_arrival}
                      onChange={(e) => {
                        if (e.target.checked && !p.new_arrival && !p.drop_alert_sent_at && !window.confirm("Flag as New Drop? This emails all Preparedness Club members an early-access alert.")) return;
                        updateProduct(p.slug, "new_arrival", e.target.checked);
                      }}
                      title="Early access — emails club members on first flag" className="accent-[#B8860B] w-4 h-4" />
                  </td>
                  <td className="p-3">
                    <input data-testid={`admin-hidden-${p.slug}`} type="checkbox" checked={!!p.hidden}
                      onChange={(e) => updateProduct(p.slug, "hidden", e.target.checked)}
                      title="Hidden products never appear on the storefront and cannot be purchased" className="accent-[#8B0000] w-4 h-4" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "subscribers" && <SubscribersTab />}

      {tab === "suppliers" && <SupplierSync />}

      {tab === "settings" && settings && (
        <div className="mt-8 max-w-xl space-y-5">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-black/50">Announcement Bar Text</label>
            <input data-testid="settings-announcement-input" value={settings.announcement} onChange={(e) => setSettings({ ...settings, announcement: e.target.value })}
              className="w-full border border-black/20 px-4 py-3 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-[#4A5D23]" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-black/50">Free Shipping Threshold ($)</label>
              <input type="number" value={settings.free_shipping_threshold} onChange={(e) => setSettings({ ...settings, free_shipping_threshold: parseFloat(e.target.value) || 0 })}
                className="w-full border border-black/20 px-4 py-3 text-sm mt-1" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-black/50">Flat Shipping ($)</label>
              <input type="number" step="0.01" value={settings.flat_shipping} onChange={(e) => setSettings({ ...settings, flat_shipping: parseFloat(e.target.value) || 0 })}
                className="w-full border border-black/20 px-4 py-3 text-sm mt-1" />
            </div>
          </div>
          <button data-testid="settings-save-btn" onClick={saveSettings} className="bg-[#4A5D23] text-white px-8 py-3.5 text-xs font-bold uppercase tracking-widest hover:bg-[#3D4D1D] transition-colors duration-150">Save Settings</button>
        </div>
      )}
    </div>
  );
}
